import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import GiftBarcodeDialog from '../components/GiftBarcodeDialog';
import './GiftList.css';

const API = process.env.REACT_APP_API_BASE_URL;

const GiftList = () => {
  const { isAuthenticated } = useAuth();
  const [summary, setSummary] = useState([]);
  const [retailer, setRetailer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pickingType, setPickingType] = useState(null);
  const [gift, setGift] = useState(null);
  const [marking, setMarking] = useState(false);
  const [activeQuery, setActiveQuery] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const authHeaders = () => ({ Authorization: `Bearer ${Cookies.get('jwt_token')}` });

  const showError = useCallback((err, fallback) => {
    let message = fallback;
    if (err.response) {
      if (err.response.status === 401) message = '認證失敗，請重新登入';
      else if (err.response.status === 500) message = '伺服器錯誤，請稍後再試';
      else if (err.response.data?.message) message = err.response.data.message;
    } else if (err.request) {
      message = '無法連接到伺服器，請檢查網路連接';
    }
    setSnackbar({ open: true, message, severity: 'error' });
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/gift/summary`, { headers: authHeaders() });
      const list = res.data.data || [];
      setSummary(list);
      setRetailer((prev) => (list.some((r) => r.retailer_type === prev) ? prev : list[0]?.retailer_type ?? null));
    } catch (err) {
      showError(err, '取得禮券資料失敗');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    if (isAuthenticated) fetchSummary();
  }, [isAuthenticated, fetchSummary]);

  // 取得該通路+類型的一張(後端固定排序,未標記使用前每次拿到同一張)
  // 沒有可用禮券(404 或空回應)回傳 null,其他錯誤往外丟
  const fetchNext = async (query) => {
    try {
      const res = await axios.get(`${API}/api/gift/next`, {
        params: { retailer_type: query.retailer, gift_type: query.type },
        headers: authHeaders()
      });
      // 後端回傳 { id, gift_text, ... },相容包在 data 裡的格式
      return res.data?.data ?? res.data ?? null;
    } catch (err) {
      if (err.response?.status === 404) return null;
      throw err;
    }
  };

  // 顯示下一張;沒有了就關閉條碼畫面並提示
  const showNext = async (query, emptyMessage) => {
    const picked = await fetchNext(query);
    if (!picked?.id) {
      setGift(null);
      setSnackbar({ open: true, message: emptyMessage, severity: 'warning' });
      return;
    }
    setGift(picked);
  };

  const handleUse = async (type) => {
    const query = { retailer, type: type.type };
    setPickingType(type.type);
    try {
      setActiveQuery(query);
      await showNext(query, '此類型已沒有可用的禮券');
    } catch (err) {
      showError(err, '取得禮券失敗');
    } finally {
      fetchSummary();
      setPickingType(null);
    }
  };

  // 標記使用後,直接換同通路+類型的下一張,不回到選擇頁
  const handleMarkUsed = async (target) => {
    setMarking(true);
    let notice = '已標記為使用完畢,已切換到下一張';
    try {
      try {
        await axios.post(`${API}/api/gift/use`, { id: target.id }, { headers: authHeaders() });
      } catch (err) {
        if (err.response?.status === 409) {
          notice = '這張禮券已經標記為使用過了,已切換到下一張';
        } else if (err.response?.status === 404) {
          setGift(null);
          setSnackbar({ open: true, message: '找不到這張禮券', severity: 'error' });
          return;
        } else {
          showError(err, '標記使用失敗');
          return;
        }
      }
      try {
        await showNext(activeQuery, '這個類型的禮券已全部使用完畢');
        setSnackbar((s) => (s.severity === 'warning' && s.open ? s : { open: true, message: notice, severity: 'success' }));
      } catch (err) {
        setGift(null);
        showError(err, '取得下一張禮券失敗');
      }
    } finally {
      fetchSummary();
      setMarking(false);
    }
  };

  const currentTypes = (summary.find((r) => r.retailer_type === retailer)?.types || []).filter((t) => t.count > 0);

  if (loading) {
    return (
      <div className="giftlist-container giftlist-center">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="giftlist-container">
      <h1 className="giftlist-title">我的禮券</h1>

      {summary.length === 0 ? (
        <p className="giftlist-empty">目前沒有可使用的禮券</p>
      ) : (
        <>
          <div className="giftlist-retailers">
            {summary.map((r) => (
              <Chip
                key={r.retailer_type}
                label={r.retailer_type}
                color={r.retailer_type === retailer ? 'primary' : 'default'}
                variant={r.retailer_type === retailer ? 'filled' : 'outlined'}
                onClick={() => setRetailer(r.retailer_type)}
                className="giftlist-chip"
              />
            ))}
          </div>

          <TableContainer component={Paper} className="giftlist-table">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>類型</TableCell>
                  <TableCell align="right">剩餘張數</TableCell>
                  <TableCell align="center" />
                </TableRow>
              </TableHead>
              <TableBody>
                {currentTypes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">此通路目前沒有可使用的禮券</TableCell>
                  </TableRow>
                ) : (
                  currentTypes.map((t) => (
                    <TableRow key={t.type}>
                      <TableCell>{t.type}</TableCell>
                      <TableCell align="right">{t.count}</TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          onClick={() => handleUse(t)}
                          disabled={pickingType !== null}
                        >
                          {pickingType === t.type ? <CircularProgress size={20} /> : '使用'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <GiftBarcodeDialog
        open={Boolean(gift)}
        gift={gift}
        onClose={() => setGift(null)}
        onMarkUsed={handleMarkUsed}
        marking={marking}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default GiftList;
